import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Loader2,
  ArrowRight,
  CheckCircle,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ForgotPasswordModal({ 
  open, 
  onClose, 
  userType = "admin",
  title = "Reset Password"
}) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/password-reset/request`, {
        email: email.toLowerCase(),
        user_type: userType
      });
      
      setSent(true);
      toast.success("If this email exists, a reset link has been sent.");
    } catch (error) {
      // Still show success message for security (don't reveal if email exists)
      setSent(true);
      toast.success("If this email exists, a reset link has been sent.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSent(false);
    setEmail("");
    onClose();
  };

  const getUserTypeLabel = () => {
    switch (userType) {
      case "hospital": return "hospital staff";
      case "contractor": return "contractor";
      case "supplier": return "supplier";
      default: return "staff";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {sent 
              ? "Check your email for a reset link"
              : `Enter your ${getUserTypeLabel()} email address`
            }
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Check Your Email</h3>
            <p className="text-slate-500 text-sm mb-4">
              If an account exists with <span className="font-medium text-slate-700">{email}</span>, 
              you will receive a password reset link within a few minutes.
            </p>
            <p className="text-xs text-slate-400 mb-4">
              Don&apos;t see it? Check your spam folder.
            </p>
            <Button onClick={handleClose} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Mail className="w-4 h-4 text-slate-400" />
                Email Address
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                data-testid="forgot-password-email"
              />
            </div>
            
            <p className="text-xs text-slate-500">
              We&apos;ll send you a secure link to reset your password. 
              The link will expire in 24 hours.
            </p>

            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                disabled={loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
                ) : (
                  <>Send Reset Link <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
